import { useEffect, useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog';

const FOLDER_COLOURS = [
  { value: '', label: 'None', hex: 'transparent' },
  { value: '#10b981', label: 'Emerald', hex: '#10b981' },
  { value: '#3b82f6', label: 'Blue', hex: '#3b82f6' },
  { value: '#f59e0b', label: 'Amber', hex: '#f59e0b' },
  { value: '#ef4444', label: 'Red', hex: '#ef4444' },
  { value: '#a855f7', label: 'Purple', hex: '#a855f7' },
  { value: '#ec4899', label: 'Pink', hex: '#ec4899' },
];

/**
 * Stand-alone dialog for renaming a saved view, editing tags, assigning
 * a folder, and picking a folder colour swatch.
 */
const EditViewDialog = ({ view, onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const [tags, setTags] = useState('');
  const [folder, setFolder] = useState('');
  const [folderColor, setFolderColor] = useState('');

  useEffect(() => {
    if (!view) return;
    setName(view.name || '');
    setTags((view.tags || []).join(', '));
    setFolder(view.folder || '');
    setFolderColor(view.folder_color || '');
  }, [view]);

  const submit = () => {
    onSubmit({
      name: name.trim(),
      tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      folder: folder.trim() || null,
      folder_color: folderColor || null,
    });
  };

  return (
    <Dialog open={Boolean(view)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-view-dialog">
        <DialogHeader>
          <DialogTitle>Edit view</DialogTitle>
          <DialogDescription>Rename, add tags, or move to a folder.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="edit-view-name">Name</Label>
            <Input
              id="edit-view-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="edit-view-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-view-tags">
              Tags <span className="text-muted-foreground text-xs">(comma-separated)</span>
            </Label>
            <Input
              id="edit-view-tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="urgent, hvac, q2"
              data-testid="edit-view-tags-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-view-folder">
              Folder <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Input
              id="edit-view-folder"
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              placeholder="Daily, Templates, Q2…"
              data-testid="edit-view-folder-input"
            />
          </div>
          {folder && (
            <div className="space-y-1.5">
              <Label>Folder colour</Label>
              <div className="flex flex-wrap gap-2">
                {FOLDER_COLOURS.map((c) => (
                  <button
                    key={c.value || 'none'}
                    type="button"
                    onClick={() => setFolderColor(c.value)}
                    className={`w-7 h-7 rounded-full border-2 transition ${
                      folderColor === c.value ? 'border-[#111565] scale-110' : 'border-transparent'
                    } ${c.value ? '' : 'bg-muted text-[10px] flex items-center justify-center text-muted-foreground'}`}
                    style={c.value ? { backgroundColor: c.hex } : {}}
                    title={c.label}
                    data-testid={`folder-color-${c.value || 'none'}`}
                  >
                    {!c.value && '×'}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            className="bg-[#111565] hover:bg-[#111565]/90"
            data-testid="edit-view-submit-btn"
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditViewDialog;
